import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldErrors } from '../forms/_shared';
import { errorMessageOf } from '../../utils/apiErrors';
import LoadingSpinner from '../LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { UNSAVED_MESSAGE, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useNotifications } from '../NotificationProvider';
import { createClient, updateClient, getClientById } from '../../services/ClientService';
import AccountFormPage from './AccountFormPage';
import ClientDetailsSection, { type ClientDetailsValues } from '../clients/ClientDetailsSection';
import ClientProjectsSection, { type OwnersState } from '../clients/ClientProjectsSection';
import { diffLinks, hasLinkChanges, linesFromClient, validateLinks, type ClientProjectLine, type ProjectOwner } from '../clients/clientProjectLines';
import { saveProjectLinks } from '../clients/clientProjectsApi';
import '../../styles/components/AccountPage.css';
import '../../styles/components/EditPersonPage.css';

interface ClientFormPageProps {
  /** Set when editing; absent for /clients/new. */
  clientId?: string | null;
}

const LIST_PATH = '/clients';
const EMPTY: ClientDetailsValues = { name: '', email: '', phone: '', notes: '' };

const same = (a: ClientDetailsValues, b: ClientDetailsValues): boolean =>
  (Object.keys(a) as (keyof ClientDetailsValues)[]).every(k => a[k].trim() === b[k].trim());

export const ClientFormPage: React.FC<ClientFormPageProps> = ({ clientId = null }) => {
  const navigate = useNavigate();
  const { workspaces, fetchProjectDetails, refreshWorkspaces } = useAuth();
  // Viewers can open a client but not change it (the API answers 403). Blind roles never see money.
  const { canEdit, canViewFinancials } = useRole();
  const { showError, showSuccess } = useNotifications();
  const savingRef = useRef(false);

  const [loading, setLoading] = useState(!!clientId);
  const [saving, setSaving] = useState(false);
  const [archived, setArchived] = useState(false);
  const [initial, setInitial] = useState<ClientDetailsValues>(EMPTY);
  const [values, setValues] = useState<ClientDetailsValues>(EMPTY);
  const [projectId, setProjectId] = useState('');
  const [initialLines, setInitialLines] = useState<ClientProjectLine[]>([]);
  const [lines, setLines] = useState<ClientProjectLine[]>([]);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [owners, setOwners] = useState<ReadonlyMap<string, ProjectOwner>>(new Map());
  const [ownersState, setOwnersState] = useState<OwnersState>('loading');
  const fieldErrors = useFieldErrors();

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    getClientById(clientId)
      .then(c => {
        if (cancelled) return;
        const loaded = { name: c.name, email: c.email ?? '', phone: c.phone ?? '', notes: c.notes ?? '' };
        const projectLines = linesFromClient(c.projects ?? [], workspaces);
        setInitial(loaded);
        setValues(loaded);
        setInitialLines(projectLines);
        setLines(projectLines);
        setArchived(!!c.archived);
      })
      .catch(err => {
        if (cancelled) return;
        showError(errorMessageOf(err, 'Could not open this client.'));
        navigate(LIST_PATH, { replace: true });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // The project list only enriches the lines once; a refresh after a save must not reload the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, navigate, showError]);

  const mayEdit = canEdit && !archived;

  // Which project belongs to which client, so the picker can warn before one is taken from another client.
  useEffect(() => {
    if (!clientId || !mayEdit || loading) return undefined;
    let cancelled = false;
    setOwnersState('loading');
    fetchProjectDetails()
      .then(rows => {
        if (cancelled) return;
        setOwners(new Map(rows.map(r => [r.project_id, { clientId: r.client_id ?? null, clientName: r.client_name ?? null }])));
        setOwnersState('ready');
      })
      .catch(() => { if (!cancelled) setOwnersState('error'); });
    return () => { cancelled = true; };
  }, [clientId, mayEdit, loading, fetchProjectDetails]);

  const changes = useMemo(() => diffLinks(initialLines, lines), [initialLines, lines]);
  const detailsDirty = !same(initial, values);
  const dirty = !archived && (detailsDirty || hasLinkChanges(changes) || (!clientId && !!projectId));
  useUnsavedChangesGuard(dirty && !saving);

  const edit = (patch: Partial<ClientDetailsValues>) => {
    setValues(v => ({ ...v, ...patch }));
    (Object.keys(patch) as (keyof ClientDetailsValues)[]).forEach(k => fieldErrors.clear(k));
  };

  const reportDetailsError = (err: unknown, fallback: string) => {
    // A 422 that names fields shows under those fields; anything else is a toast.
    if (!fieldErrors.setFromError(err)) showError(errorMessageOf(err, fallback));
    else showError(errorMessageOf(err, 'Please check the highlighted fields.'));
  };

  const handleSave = async () => {
    if (!values.name.trim() || savingRef.current) return;
    const fields = {
      name: values.name.trim(),
      email: values.email.trim() || undefined,
      phone: values.phone.trim() || undefined,
      notes: values.notes.trim() || undefined,
    };
    fieldErrors.clear();
    setSummary(null);

    if (!clientId) {
      savingRef.current = true;
      setSaving(true);
      try {
        await createClient({ ...fields, project_id: projectId || undefined });
        showSuccess('Client added.');
        navigate(LIST_PATH);
      } catch (err) {
        reportDetailsError(err, 'Failed to save client.');
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
      return;
    }

    const problems = validateLinks(lines);
    setLineErrors(problems);
    if (Object.keys(problems).length > 0) {
      setSummary('Some things need fixing before this can be saved. They are marked below.');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      // Today's rule: Save always writes the details; with project changes it writes them only when they changed.
      let detailsFailed = false;
      if (detailsDirty || !hasLinkChanges(changes)) {
        try { await updateClient(clientId, fields); } catch (err) { detailsFailed = true; reportDetailsError(err, 'Failed to save client.'); }
      }
      const result = hasLinkChanges(changes)
        ? await saveProjectLinks(clientId, changes)
        : { errors: {}, addedOk: new Set<string>(), removedOk: new Set<string>(), saved: 0, failed: 0 };
      if (result.saved > 0) await refreshWorkspaces().catch(() => { /* the saved links stand; the list refreshes on the next load */ });

      if (!detailsFailed && result.failed === 0) {
        showSuccess('Client updated.');
        navigate(LIST_PATH);
        return;
      }

      // Stay on the page: what saved is now the baseline, what failed stays on screen with its message.
      if (!detailsFailed) setInitial(values);
      const failedRemovals = changes.remove.filter(l => result.errors[l.key]);
      setInitialLines([
        ...initialLines.filter(l => !result.removedOk.has(l.projectId)),
        ...lines.filter(l => result.addedOk.has(l.projectId)),
      ]);
      setLines([...lines, ...failedRemovals]);
      setLineErrors(result.errors);
      if (result.failed > 0) {
        setSummary(`${result.saved} ${result.saved === 1 ? 'project change was' : 'project changes were'} saved and ${result.failed} could not be. The ones that failed stay on this page, marked; fix them and save again.`);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const leave = () => {
    // Links, Back and reload are covered by the guard; Cancel is a button, so it asks itself.
    if (dirty && !window.confirm(UNSAVED_MESSAGE)) return;
    navigate(LIST_PATH);
  };

  if (loading) return <LoadingSpinner />;

  const readOnly = archived || !canEdit;
  const title = clientId ? (values.name || 'Client') : 'New client';
  const projectChoices = !clientId && workspaces.length > 0
    ? workspaces.map(w => ({ value: w.project_id, label: w.project_name }))
    : undefined;

  return (
    <AccountFormPage
      view="client-form"
      leaf={clientId ? (initial.name || 'Client') : title}
      title={clientId ? 'Edit client' : 'New client'}
      subtitle={clientId ? 'Details and the projects for this client.' : 'Details for the new client.'}
      onSave={() => void handleSave()}
      onCancel={leave}
      saveLabel={clientId ? 'Save' : 'Add client'}
      canSave={!!values.name.trim()}
      saving={saving}
      readOnly={readOnly}
    >
      {archived && <p className="ap-note">This client is archived. Restore it from the Clients list to edit it.</p>}
      {!archived && !canEdit && <p className="ap-note">Your role can view clients but not change them.</p>}
      {summary && <p className="ep-summary" role="alert">{summary}</p>}
      <ClientDetailsSection
        values={values}
        errorFor={k => fieldErrors.errorFor(k)}
        onChange={edit}
        disabled={readOnly || saving}
        projectChoices={projectChoices}
        projectId={projectId}
        onProjectChange={setProjectId}
      />
      {clientId ? (
        <ClientProjectsSection
          clientId={clientId}
          lines={lines}
          onChange={next => { setLines(next); setLineErrors({}); }}
          workspaces={workspaces}
          owners={owners}
          ownersState={ownersState}
          errors={lineErrors}
          showMoney={canViewFinancials}
          disabled={readOnly || saving}
        />
      ) : (
        <section className="settings-card ep-section" aria-label="Projects">
          <h2 className="ap-section-title">Projects</h2>
          <p className="settings-muted-xs">Projects can be added after the client is created.</p>
        </section>
      )}
    </AccountFormPage>
  );
};

export default ClientFormPage;
